import styled from 'styled-components';
import { BUTTON_SIZES, GhostButton, PrimaryButton } from 'ui/button';
import { InputText } from 'ui/inputs/inputText/InputText';
import { Menu } from 'ui/menu/Menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { Add24 } from 'ui/icons/action/Add24';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { usePaginationContext } from 'utils/contexts/pagination';
import {
  useFiltersContext,
  ORG_MEMBER,
  ORG_ADMIN,
  PROJECT_MEMBERSHIP,
  MFA_ENABLED,
  MFA_DISABLED,
  Filters,
} from '../../contexts/FiltersContext';
import {
  useOrganizationModalsContext,
  MODAL_NAMES,
} from '../../contexts/OrganizationModalsContext';
import { useOrganizationMembersQueryContext } from '../../contexts/OrganizationMembersQueryContext';
import { Project } from '../../types';

const InlineFlexContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  margin: ${pxToRem(20)} 0;
`;

const projectMembershipOptionId = (projectId: number) => `${PROJECT_MEMBERSHIP}_${projectId}`;

const getSelectedOptionIds = (filters: Filters) => {
  const selectedProjectMembershipOptionIds = [...filters[PROJECT_MEMBERSHIP].keys()].map(
    projectMembershipOptionId,
  );
  return [
    ...[ORG_MEMBER, ORG_ADMIN, MFA_ENABLED, MFA_DISABLED].filter(
      (key) => filters[key as keyof Filters],
    ),
    ...selectedProjectMembershipOptionIds,
  ];
};

export const PageActions = () => {
  const { showModal } = useOrganizationModalsContext();
  const { projects = [] } = useOrganizationMembersQueryContext();
  const { currentPage, setCurrentPage } = usePaginationContext();
  const {
    search,
    setSearch,
    filters,
    toggleProjectMembershipFilter,
    toggleFilter,
    resetFilters,
    numFiltersApplied,
  } = useFiltersContext();

  const selectedOptionIds = getSelectedOptionIds(filters);

  return (
    <InlineFlexContainer>
      {!!numFiltersApplied && (
        <GhostButton size={BUTTON_SIZES.LARGE} onClick={resetFilters}>
          Clear
        </GhostButton>
      )}
      <Menu
        id="members-table-filter-menu"
        options={[
          {
            id: 'organization-role-filter',
            label: 'Organization role',
            options: [
              {
                id: ORG_MEMBER,
                label: 'Member',
              },
              {
                id: ORG_ADMIN,
                label: 'Administrator',
              },
            ],
          },
          {
            id: 'project-membership-filter',
            hasFilter: true,
            options: projects.map((project: Project) => ({
              id: projectMembershipOptionId(project.id),
              label: project.name,
            })),
            label: 'Project membership',
            hasCountBadge: !!filters[PROJECT_MEMBERSHIP].size,
          },
          {
            id: 'mfa-filter',
            options: [
              {
                id: MFA_ENABLED,
                label: 'Enabled',
              },
              {
                id: MFA_DISABLED,
                label: 'Disabled',
              },
            ],
            label: 'MFA',
          },
        ]}
        defaultSelectedOptionIds={selectedOptionIds}
        handleSelectionChange={(selectedId: string) => {
          if (currentPage !== 1) setCurrentPage(1);
          if (selectedId.includes(PROJECT_MEMBERSHIP)) {
            const projectId = parseInt(selectedId.split(`${PROJECT_MEMBERSHIP}_`)[1], 10);
            toggleProjectMembershipFilter(projectId);
          } else {
            toggleFilter(selectedId as keyof Filters);
          }
        }}
        horizontalAlignment={ALIGNMENT.LEFT}
        isSelectMenu={true}
        hasFilter={false}
      >
        <FilterMenuTriggerButton selectedFilterCount={numFiltersApplied || 0} />
      </Menu>
      <InputText
        id="search-members"
        placeholder="Search members"
        value={search}
        onChange={(val: string) => {
          if (currentPage !== 1) setCurrentPage(1);
          setSearch(val);
        }}
      />
      <PrimaryButton
        size={BUTTON_SIZES.LARGE}
        onClick={() => {
          showModal({
            name: MODAL_NAMES.NEW_MEMBER,
          });
        }}
      >
        <Add24 />
        New member
      </PrimaryButton>
    </InlineFlexContainer>
  );
};
